import { getPredecessorCandidates, useQuery } from "wasp/client/operations";
import { Label } from "../../client/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../client/components/ui/select";
import useTranslation from "../../client/hooks/useTranslation";
import PredecessorLabel, { type PredecessorOption } from "./PredecessorLabel";

const NONE = "__none__";

interface Props {
  accountId: string;
  /** undefined = pas touché (affiche le lien en base), null = « Aucun ». */
  value: string | null | undefined;
  onChange: (value: string | null) => void;
}

/**
 * Champ « Challenge d'origine » de la modale de modification (Lite et
 * Advanced). Contrôlé : la modale envoie le choix à l'enregistrement via
 * setFundedPredecessor. Rien n'est pré-sélectionné hors du lien existant : la
 * suggestion n'est qu'une étiquette, jamais un lien posé en silence.
 */
export default function PredecessorSelectField({ accountId, value, onChange }: Props) {
  const t = useTranslation();
  const { data } = useQuery(getPredecessorCandidates, { accountId });

  if (!data || !data.linkable) return null;

  const current = data.current as { id: string; name: string } | null;
  const candidates = data.candidates as PredecessorOption[];
  const selected = value === undefined ? (current?.id ?? NONE) : (value ?? NONE);
  const changed = value !== undefined && (value ?? null) !== (current?.id ?? null);

  return (
    <div className="space-y-2">
      <Label>{t.fundedLineage.cardTitle}</Label>
      <Select value={selected} onValueChange={(v) => onChange(v === NONE ? null : v)}>
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>{t.fundedLineage.modalNone}</SelectItem>
          {current && (
            <SelectItem value={current.id}>
              <span className="font-mono">{current.name}</span>
            </SelectItem>
          )}
          {candidates.map((c) => (
            <SelectItem key={c.id} value={c.id}>
              <PredecessorLabel option={c} suggested={c.id === data.suggestedId} />
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {candidates.length === 0 && !current ? (
        <p className="text-xs text-muted-foreground">{t.fundedLineage.cardNoCandidates}</p>
      ) : (
        <p className="text-xs text-muted-foreground">
          {t.fundedLineage.modalLinkHint}
          {(current || changed) && ` ${t.fundedLineage.modalChangeHint}`}
        </p>
      )}
    </div>
  );
}
